/* import __COLOCATED_TEMPLATE__ from './install-and-assign-permissions.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { type PulseAccordion } from 'glint/pulse';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type SalesforceConfiguration from 'embercom/models/standalone/salesforce-configuration';
import type IntlService from 'embercom/services/intl';
import { type SalesforceSetupData } from 'embercom/objects/standalone/salesforce';
import { taskFor } from 'ember-concurrency-ts';
import { action } from '@ember/object';

interface Args {
  accordion: PulseAccordion;
  onContinue: () => void;
  onCasesChannel?: boolean;
}

interface Signature {
  Args: Args;
}

export default class InstallAndAssignPermissionsComponent extends Component<Signature> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get salesforceConfig(): SalesforceConfiguration {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get salesforceSetupData(): SalesforceSetupData {
    return this.salesforcePlatform.salesforceSetupData;
  }

  get isDisabled() {
    return Boolean(this.salesforceConfig?.isPendingSetup);
  }

  get isInProgress() {
    return (
      this.salesforceSetupData.hasPermissionSet &&
      !this.salesforceSetupData.hasPermissionSetAssignedToInstaller
    );
  }

  get isComplete() {
    return (
      this.salesforceSetupData.hasPermissionSet &&
      this.salesforceSetupData.hasPermissionSetAssignedToInstaller
    );
  }

  get isLoading() {
    return (
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceSetupData ||
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforcePermissions ||
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforcePermissionsHealthcheck
    );
  }

  get hasFailedPermissionsHealthcheck() {
    return this.salesforceSetupData.permissionsHealthcheck.any(
      (healthcheck) => healthcheck.status !== 'ok',
    );
  }

  get failingHealthchecks() {
    return this.salesforceSetupData.permissionsHealthcheck.filter(
      (healthcheck) => healthcheck.status !== 'ok',
    );
  }

  @action
  reloadPermissionsHealthcheck() {
    taskFor(
      this.salesforcePlatform.salesforceDataLoader.loadSalesforcePermissionsHealthcheck,
    ).perform();
  }

  @action
  async handleVisibilityChange() {
    if (
      document.visibilityState === 'hidden' ||
      (this.salesforceSetupData.hasPermissionSet &&
        this.salesforceSetupData.hasPermissionSetAssignedToInstaller)
    ) {
      return;
    }

    await taskFor(
      this.salesforcePlatform.salesforceDataLoader.checkSalesforcePermissions,
    ).perform();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Cases::Connect::InstallAndAssignPermissions': typeof InstallAndAssignPermissionsComponent;
  }
}
